<script setup lang="ts">
type OwnerRegistrationStepOneFormProps = {
    submit: () => void;
};

type OwnerRegistrationStepTwoFormProps = {
    submit: () => void;
};

type StepperComponentExposed = {
    setCurrent: (currentIndex: number) => void;
};

const store = useOwnerRegisterStore();
const { $clearForms, $setFormDashboardUrlData } = store
const ownerRegistrationStepOneFormRef = ref<OwnerRegistrationStepOneFormProps | null>(null);
const ownerRegistrationStepTwoFormRef = ref<OwnerRegistrationStepTwoFormProps | null>(null);
const stepperRef = ref<StepperComponentExposed | null>(null);
const appConfig = useAppConfig()
const runtimeConfig = useRuntimeConfig()


useSeoMeta({
    title: `Restaurant Owner Registration | ${appConfig.title} - Sign Up Now`,
    ogTitle: `Restaurant Owner Registration | ${appConfig.title} - Sign Up Now`,
    description: `Register as a restaurant owner on Kichieats! Enter your phone number and email address to create your account and start enjoying the benefits of our platform. Connect with diners and manage your restaurant easily!`,
    ogDescription: `Register as a restaurant owner on Kichieats! Enter your phone number and email address to create your account and start enjoying the benefits of our platform. Connect with diners and manage your restaurant easily!`,
    ogImage: appConfig.defaultSeoImg,
    twitterCard: 'summary_large_image',
    twitterImage: `https://img.kichieats.ph/defaultSeoImg.png`,
})

definePageMeta({
    layout: 'dashboard',
})

const formSteps = [
    {
        key: 1,
        status: 1,
        title: 'Customer Information',
    },
    {
        key: 2,
        status: 0,
        title: 'Store Information',
    },
    {
        key: 3,
        status: 0,
        title: 'Confirmation',
    },
    {
        key: 4,
        status: 0,
        title: 'Completion',
    }
]

const current = ref<number>(1);

const next = async () => {
    if (current.value == 1) {
        if (ownerRegistrationStepOneFormRef.value && ownerRegistrationStepOneFormRef.value.submit) {
            ownerRegistrationStepOneFormRef.value.submit();
        } else {
            console.error('submit method not found on child component');
        }
    }

    if (current.value == 2) {
        if (ownerRegistrationStepTwoFormRef.value && ownerRegistrationStepTwoFormRef.value.submit) {
            ownerRegistrationStepTwoFormRef.value.submit();
        } else {
            console.error('submit method not found on child component');
        }
    }

    const owner = computed(() => ({
        firstname: store.formOneData?.firstname || null,
        lastname: store.formOneData?.lastname || null,
        birthday: store.formOneData?.birthday || null,
        email: store.formOneData?.email || null,
        contact: store.formOneData?.contact || null,
        password: store.formOneData?.password || null,
        password_confirmation: store.formOneData?.password_confirmation || null,
    }));

    const restaurant = computed(() => ({
        name: store.formTwoData?.name || null,
        address1: store.formTwoData?.address1 || null,
        region_id: store.formTwoData?.region_id?.value || null,
        city_id: store.formTwoData?.city_id?.value || null,
        barangay_id: store.formTwoData?.barangay_id?.value || null,
        categories: store.formTwoData?.categories?.map((y: any) => y.value).join(',') || null,
        contact: store.formTwoData?.contact || null,
        plan_id: store.formTwoData?.planType?.value || null,
    }));

    const pm = computed(() => ({
        card_number: store.formTwoData?.card_number || null,
        exp_month: dateFormat(store.formTwoData?.exp_month, 'month') || null,
        exp_year: dateFormat(store.formTwoData?.exp_year, 'year') || null,
        card_holder_name: store.formTwoData?.card_holder_name || null,
        cvc: store.formTwoData?.cvc || null,
    }));

    if (current.value == 3) {
        console.log('Owner:', owner.value);
        console.log('Restaurant:', restaurant.value);

        try {
            const { data, status }: any = await useApi(`/api/owner/restaurant-register`, {
                method: 'POST',
                body: {
                    owner: owner.value,
                    restaurant: restaurant.value,
                    pm: pm.value
                }
            })

            if (data && status == 'success') {
                $setFormDashboardUrlData(data.dashboard_url)
                if (current.value < formSteps.length) {
                    if (stepperRef.value && stepperRef.value.setCurrent) {
                        current.value++;
                        stepperRef.value.setCurrent(current.value);
                    } else {
                        console.error('next method not found on child component');
                    }
                }
                else {
                    console.warn('Already at the last step, cannot move next');
                }
            }

        } catch (error: any) {
            if (error.response) {
                const { statusCode, errorMessage, serverErrorMessage, serverErrorObject } = error.response._data?.error || {};

                console.error('Error', {
                    statusCode: statusCode,
                    errorMessage: errorMessage,
                    serverErrorMessage: serverErrorMessage,
                    serverErrorObject: serverErrorObject
                })

                showNotificationError(serverErrorMessage);

            } else {
                console.error("Unexpected error:", error);
            }
        }

    }

    // if(current.value == 4) {

    // }
};

const prev = () => {
    if (current.value > 0) {
        if (stepperRef.value && stepperRef.value.setCurrent) {
            current.value--;
            stepperRef.value.setCurrent(current.value);
        } else {
            console.error('prev method not found on child component');
        }
    }
    else {
        console.warn('Already at the first step, cannot move previous');
    }
};

const handleSetCurrentEvent = ($event: number) => {
    if (stepperRef.value && stepperRef.value.setCurrent) {
        current.value = $event;
        stepperRef.value.setCurrent($event);
    }
    else {
        console.error('setCurrent method not found on child component');
    }
}

const handleSubmitEvent = () => {
    if (current.value < formSteps.length) {
        if (stepperRef.value && stepperRef.value.setCurrent) {
            current.value++;
            stepperRef.value.setCurrent(current.value);
        } else {
            console.error('next method not found on child component');
        }
    }
    else {
        console.warn('Already at the last step, cannot move next');
    }

}



$clearForms();
const viewport = useViewport()

</script>

<template>
    <Block class="mx-auto max-w-[116rem] px-[2rem]">

        <Section class="flex mt-[2.4rem] pb-[8rem] gap-[4rem] items-start w-full" v-if="viewport.isGreaterThan('sp')">
            <Section class="w-full">
                <TitleText type="2">
                    Restaurant Owner Registration
                </TitleText>

                <Block class="mt-[2.4rem]">
                    <Stepper ref="stepperRef"
                        class="max-w-[100rem] m-auto stepper text-[1.6rem] leading-[1.5] font-bold" :disableClick="true"
                        :modelValue="formSteps" @setCurrentEvent="handleSetCurrentEvent($event)" />

                    <Block v-if="current == 1">
                        <OwnerRegistrationStepOneForm ref="ownerRegistrationStepOneFormRef"
                            @submitEvent="handleSubmitEvent" />
                    </Block>

                    <Block v-if="current == 2">
                        <OwnerRegistrationStepTwoForm ref="ownerRegistrationStepTwoFormRef"
                            @submitEvent="handleSubmitEvent" />
                    </Block>

                    <Block v-if="current == 3">
                        <OwnerRegistrationStepThreeForm />
                    </Block>

                    <Block v-if="current == 4">
                        <OwnerRegistrationStepFourForm />
                    </Block>

                    <Block
                        class="flex justify-center gap-[2.4rem] max-md:flex-col max-md:gap-[1.2rem] max-md:items-center"
                        v-if="current !== 4">
                        <NuxtLink v-if="current == 1" to="/" type="button"
                            class="mt-[2.4rem] flex items-center justify-center  text-primary font-bold text-[1.4rem] leading-[1.5] rounded-[1rem] p-[1rem] max-w-[25.2rem] w-full border border-[darkGray] divide-[darkGray] hover:border-darkGray hover:bg-bgSecond transition ease-in-out delay-150">
                            <!-- <figure class="max-w-[2rem] inline-block mr-[.8rem]">
                                <img class="w-full" src="/img/common/ico_backArrow.png" alt="back">
                            </figure> -->
                            Back to Home
                        </NuxtLink>

                        <button v-if="current >= 2" @click="prev()" type="button"
                            class="mt-[2.4rem] flex items-center justify-center  text-primary font-bold text-[1.4rem] leading-[1.5] rounded-[1rem] p-[1rem] max-w-[25.2rem] w-full border border-[darkGray] divide-[darkGray] hover:border-darkGray hover:bg-bgSecond transition ease-in-out delay-150">
                            Previous
                        </button>

                        <button @click="next()" type="submit"
                            class="mt-[2.4rem] bg-[goldenYellow] text-primary font-bold text-[1.4rem] leading-[1.5] rounded-[1rem] p-[1rem] max-w-[25.2rem] w-full max-md:mt-0">Next
                        </button>
                    </Block>

                </Block>
            </Section>
        </Section>

        <section v-else>

            <div class="sp-only-notice">
                <strong>This registration page is only available on PC.</strong><br>
                Unfortunately, smartphone registration is not supported.<br>
                Please access the “Restaurant Owner Registration” page from a PC to complete your registration.
            </div>
        </section>
    </Block>
</template>

<style scoped>
.sp-only-notice {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeeba;
    padding: 1rem;
    text-align: center;
    font-size: 1rem;
    border-radius: 8px;
    max-width: 600px;
    margin: 2rem auto;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
</style>